"use strict";
/**
 * Partner Network: the referral network between mortgage brokers and a
 * referral PARTNER (first partner: a buyer's agent). Design and rationale:
 * docs/plans/2026-09-26-PARTNER-NETWORK-PORTAL.md.
 *
 * This module is pure: schemas, the fee engine, and the referral stage
 * machine. No I/O, no clock, no randomness, so every figure it produces is
 * reproducible from its inputs (Golden Principle 1).
 *
 * Money is integer cents throughout (root CLAUDE.md §6). Rates are integer
 * basis points (1000 = 10%). No float ever enters a fee calculation.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PartnerLedgerEntryKindSchema = exports.PARTNER_LEDGER_ENTRY_KINDS = exports.PartnerClientRewardSchema = exports.PartnerCrmStageMapSchema = exports.DEFAULT_PARTNER_DUPLICATE_RULE = exports.PartnerDuplicateRuleSchema = exports.DuplicateRuleModeSchema = exports.DUPLICATE_RULE_MODES = exports.ReferralCrmLinkSchema = exports.ReferralVerificationSchema = exports.REFERRAL_VERIFICATION_STATES = exports.PRICE_REQUIRED_STAGES = exports.AdvanceReferralInputSchema = exports.ReferrerRefSchema = exports.CreatePartnerReferralInputSchema = exports.REFERRAL_STAGE_LABELS = exports.ReferralStageSchema = exports.REFERRAL_STAGES = exports.REFERRAL_PIPELINE = exports.FEE_SCHEDULE_TEMPLATES = exports.STANDARD_FEE_SCHEDULE_LABEL = exports.FeeScheduleVariantSchema = exports.FeeScheduleVariantKeySchema = exports.FEE_SCHEDULE_VARIANT_LIMIT = exports.PartnerFeeScheduleSchema = exports.PAYABLE_STAGES = exports.FeeRuleSchema = exports.FeeComponentSchema = exports.FeeBandSchema = exports.FEE_BASE_LABELS = exports.FeeBaseSchema = exports.FEE_BASES = exports.MAX_CENTS = exports.PropertyTypeSchema = exports.PROPERTY_TYPES = exports.PartnerClaimsSchema = exports.PARTNER_DEAL_CANDIDATE_GRANT_TTL_MS = exports.PARTNER_DEAL_CANDIDATE_GRANTS = exports.PARTNER_PORTAL_REDACTED_REFERRAL_FIELDS = exports.PARTNER_DESK_TENANT_ROLES = exports.PARTNER_DESK_CAPABILITIES = exports.PARTNER_REFER_OUT_FLAG = exports.PARTNER_NETWORK_TENANT_ROLES = exports.PARTNER_DESK_ROLES = exports.PartnerRoleSchema = exports.PARTNER_ROLES = void 0;
exports.isPartnerDeskRole = isPartnerDeskRole;
exports.partnerReferOutEnabled = partnerReferOutEnabled;
exports.partnerDeskCapabilities = partnerDeskCapabilities;
exports.partnerDealCandidateGrantId = partnerDealCandidateGrantId;
exports.dealCandidateGrantAllows = dealCandidateGrantAllows;
exports.feeScheduleKeyFromLabel = feeScheduleKeyFromLabel;
exports.applyRateBps = applyRateBps;
exports.percentOfBps = percentOfBps;
exports.quoteStagePayout = quoteStagePayout;
exports.quoteReferralFee = quoteReferralFee;
exports.totalPayoutIncGst = totalPayoutIncGst;
exports.isTerminalStage = isTerminalStage;
exports.canTransition = canTransition;
exports.nextStage = nextStage;
exports.referrerUid = referrerUid;
exports.referralVerification = referralVerification;
exports.partnerDuplicateRule = partnerDuplicateRule;
exports.splitSettlementCents = splitSettlementCents;
exports.ledgerEntryKind = ledgerEntryKind;
const zod_1 = require("zod");
// ─── Identity ────────────────────────────────────────────────────────────────
/**
 * A partner identity carries its OWN claim namespace and deliberately NO
 * `role` and NO `companyId`. `enforceAuthenticated()` rejects any token
 * without both, so every tenant callable fails closed for a partner user by
 * construction, and no tenant Firestore rule keyed on `companyId` can match.
 */
exports.PARTNER_ROLES = ['partner_admin', 'partner_staff', 'partner_referrer'];
exports.PartnerRoleSchema = zod_1.z.enum(exports.PARTNER_ROLES);
/** Partner roles that see every referral addressed to the partner, so they must hold MFA. */
exports.PARTNER_DESK_ROLES = ['partner_admin', 'partner_staff'];
function isPartnerDeskRole(role) {
    return exports.PARTNER_DESK_ROLES.includes(role);
}
/**
 * The TENANT roles that may use the Partner Network from apps/client (refer,
 * list their referrals, list partners). An allowlist, so a role added later
 * stays out until someone decides otherwise. marketing (no commission scope),
 * service_staff (assigned leads only) and loyalty_client (a consumer) are
 * deliberately absent. The client nav/route guard and the loyalty callables
 * both read THIS constant, so the UI and the server can never disagree.
 */
exports.PARTNER_NETWORK_TENANT_ROLES = [
    'super_admin',
    'administrator',
    'office_admin',
    'credit_analyst',
    'broker',
    'broker_assistant',
];
/**
 * The tenant switch for the REFER-OUT side (refer a client to another
 * partner, my clients, rewards, back book radar, compliance). `partner_network`
 * opens the Partner Network as a whole; a tenant that runs its own referral
 * program and never refers out (a buyer's-agent network was the refer-out
 * case) sets `featureFlags.partner_network_refer_out` to false, which closes
 * the refer-out side in the UI and on every refer-out callable while its own
 * program desk stays open. Absent or anything but false keeps refer-out on, so
 * tenants that already refer out are unchanged. Turning it off deletes nothing.
 */
exports.PARTNER_REFER_OUT_FLAG = 'partner_network_refer_out';
/** Is the refer-out side open for these feature flags? Only an explicit false closes it. */
function partnerReferOutEnabled(featureFlags) {
    return featureFlags?.[exports.PARTNER_REFER_OUT_FLAG] !== false;
}
// ─── Desk access: one desk, two doors ────────────────────────────────────────
// docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §16. A program run by a
// VALUTTEN tenant (partner doc `operatingCompanyId` set) is worked from inside
// VALUTTEN by tenant users; a standalone partner is worked from the partner
// portal by partner accounts. Both doors reach the same desk callables, which
// check ONE capability each through enforceDeskAccess (loyalty-functions).
/**
 * Everything the desk can do, one capability per kind of action:
 *   referrals.read      list and open referrals (the desk view)
 *   referrals.verify    link a website referral to a member
 *   referrals.invite    invite a website referrer
 *   referrals.reject    reject a referral, resolve a duplicate
 *   referrals.stage     move a referral to a stage that needs no price
 *   referrals.price     move a referral under contract or to settled (records the price)
 *   members.read        the member list (needed to pick who to link)
 *   members.manage      invite, update, suspend, resend, cancel members
 *   applications.read   the join-form queue
 *   applications.manage approve or decline an application
 *   fees.read           fee cards and the fee figures on a referral
 *   fees.manage         edit fee cards, assign a member's card
 *   payouts.read        the ledger, gift cards, the float
 *   payouts.manage      mark paid, issue or cancel gift cards, record a top-up, client rewards
 *   settings.read       duplicate rule, client reward
 *   settings.manage     change them
 *   crm.link            CRM deal candidates, link and unlink a deal, the CRM stage map
 *                       (tenant door only: the partner platform never touches tenant loan data)
 */
exports.PARTNER_DESK_CAPABILITIES = [
    'referrals.read',
    'referrals.verify',
    'referrals.invite',
    'referrals.reject',
    'referrals.stage',
    'referrals.price',
    'members.read',
    'members.manage',
    'applications.read',
    'applications.manage',
    'fees.read',
    'fees.manage',
    'payouts.read',
    'payouts.manage',
    'settings.read',
    'settings.manage',
    'crm.link',
];
/**
 * The tenant roles that work a tenant-operated program's desk inside VALUTTEN:
 * super_admin and administrator (the full desk, including fees, payouts,
 * settings and CRM linking) and marketing (verify, invite, reject, resolve
 * duplicates, move stages; no fees, payouts, settings or CRM). An allowlist:
 * a role added later stays out until someone decides otherwise.
 */
exports.PARTNER_DESK_TENANT_ROLES = ['super_admin', 'administrator', 'marketing'];
const ALL_EXCEPT_CRM = exports.PARTNER_DESK_CAPABILITIES.filter((c) => c !== 'crm.link');
/**
 * The capability table. Partner door rows reproduce, callable by callable, the
 * role lists the partner portal enforced before the desk had two doors, so a
 * standalone partner's staff can do exactly what they could before.
 */
const DESK_CAPABILITY_TABLE = {
    partner: {
        partner_admin: ALL_EXCEPT_CRM,
        partner_staff: [
            'referrals.read',
            'referrals.verify',
            'referrals.reject',
            'referrals.stage',
            'referrals.price',
            'applications.read',
            'fees.read',
            'payouts.read',
            'settings.read',
        ],
    },
    tenant: {
        super_admin: exports.PARTNER_DESK_CAPABILITIES,
        administrator: exports.PARTNER_DESK_CAPABILITIES,
        marketing: [
            'referrals.read',
            'referrals.verify',
            'referrals.invite',
            'referrals.reject',
            'referrals.stage',
            'members.read',
            'applications.read',
            'applications.manage',
        ],
    },
};
/** The capabilities a role has at a door; empty for a role that has no desk there. Never throws. */
function partnerDeskCapabilities(door, role) {
    if (typeof role !== 'string')
        return [];
    const row = Object.prototype.hasOwnProperty.call(DESK_CAPABILITY_TABLE[door], role)
        ? DESK_CAPABILITY_TABLE[door][role]
        : undefined;
    return row ? [...row] : [];
}
/**
 * Referral fields the partner PORTAL never returns, on the desk view and the
 * referrer view alike (§16 item 2): loan amount, purchase price and every CRM
 * field. Lender and adviser are never stored on a referral. Inside VALUTTEN
 * the tenant desk may show the fee inputs to callers who hold `fees.read`.
 */
exports.PARTNER_PORTAL_REDACTED_REFERRAL_FIELDS = ['loanAmountCents', 'purchasePriceCents', 'crmLink'];
/**
 * Linking a referral to a CRM deal is a tenant operation (§16 item 2). The
 * tenant callable that lists deal candidates for one referral (client-functions
 * tenantGetPartnerReferralDealCandidates: broker scope, loan-value policy,
 * audited, rate limited) records which deals it showed to whom in
 * `partner_deal_candidate_grants/{referralId}__{uid}` (server-only). The desk's
 * link action (loyalty partnerLinkCrmDeal) accepts only a deal in that caller's
 * unexpired grant, so a link can never reach a deal the caller could not see,
 * and the partner platform never queries tenant loan data itself.
 */
exports.PARTNER_DEAL_CANDIDATE_GRANTS = 'partner_deal_candidate_grants';
exports.PARTNER_DEAL_CANDIDATE_GRANT_TTL_MS = 30 * 60 * 1000;
function partnerDealCandidateGrantId(referralId, uid) {
    const safe = (s) => s.replace(/[^A-Za-z0-9_-]/g, '_');
    return `${safe(referralId)}__${safe(uid)}`;
}
/** Whether a stored grant lets this caller link this deal now. Pure; `nowMs` from the caller. */
function dealCandidateGrantAllows(raw, want, nowMs) {
    const g = raw;
    if (!g || g.companyId !== want.companyId || g.partnerId !== want.partnerId || g.referralId !== want.referralId || g.uid !== want.uid) {
        return null;
    }
    if (typeof g.expiresAtMs !== 'number' || g.expiresAtMs <= nowMs || !Array.isArray(g.deals))
        return null;
    const deal = g.deals.find((d) => d && d.dealId === want.dealId && typeof d.provider === 'string');
    return deal ? { dealId: deal.dealId, provider: deal.provider } : null;
}
exports.PartnerClaimsSchema = zod_1.z
    .object({
    partnerRole: exports.PartnerRoleSchema,
    partnerId: zod_1.z.string().min(1).max(128),
    partnerMemberId: zod_1.z.string().min(1).max(128),
})
    .strict();
// ─── Fee schedule ────────────────────────────────────────────────────────────
exports.PROPERTY_TYPES = ['investment', 'commercial', 'owner_occupied'];
exports.PropertyTypeSchema = zod_1.z.enum(exports.PROPERTY_TYPES);
/**
 * $100M ceiling on any money input. Far above any residential purchase, and
 * low enough that every product the engine forms (amount x (10000 + bps))
 * stays well inside Number.MAX_SAFE_INTEGER, so integer maths stays exact.
 */
exports.MAX_CENTS = 100_000_000_00;
const cents = zod_1.z.number().int().nonnegative().max(exports.MAX_CENTS);
const bps = zod_1.z.number().int().nonnegative().max(100_00);
/**
 * THE FEE ENGINE IS GENERIC. Every buyer's agent (or other partner) pays
 * differently, so a schedule is data, never code. A payout rule is a sum of
 * components, each of which is:
 *   fixed    a set amount
 *   percent  a rate of one base: the partner's own fee, the purchase price, or the loan
 *   banded   a set amount chosen by which band the purchase price (or loan) falls in
 * then an optional floor and cap. Colin Lee's card (a fixed fee stepped by
 * price) is one banded configuration; "20% of our fee, minimum $2,000" is a
 * percent with a floor; "$500 plus 0.1% of the price, capped at $3,000" is
 * fixed + percent with a cap. Templates below give a partner a starting point.
 *
 * Order of operations, identical for the partner's own fee and every payout:
 *   sum components -> loading (if it applies to this fee) -> floor/cap -> GST.
 * A price above the last bounded band uses the last band.
 */
exports.FEE_BASES = ['agent_fee', 'purchase_price', 'loan_amount'];
exports.FeeBaseSchema = zod_1.z.enum(exports.FEE_BASES);
exports.FEE_BASE_LABELS = {
    agent_fee: "The partner's own fee",
    purchase_price: 'Purchase price',
    loan_amount: 'Loan amount',
};
exports.FeeBandSchema = zod_1.z
    .object({
    /** Inclusive upper bound of the base, in cents. `null` = open-ended last band. */
    upToCents: zod_1.z.number().int().positive().max(exports.MAX_CENTS).nullable(),
    amountCents: cents,
})
    .strict();
exports.FeeComponentSchema = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({ kind: zod_1.z.literal('fixed'), amountCents: cents }).strict(),
    zod_1.z.object({ kind: zod_1.z.literal('percent'), base: exports.FeeBaseSchema, rateBps: bps }).strict(),
    zod_1.z
        .object({
        kind: zod_1.z.literal('banded'),
        base: zod_1.z.enum(['purchase_price', 'loan_amount']),
        bands: zod_1.z.array(exports.FeeBandSchema).min(1).max(20),
    })
        .strict(),
]);
exports.FeeRuleSchema = zod_1.z
    .object({
    components: zod_1.z.array(exports.FeeComponentSchema).min(1).max(5),
    floorCents: cents.nullable(),
    capCents: cents.nullable(),
})
    .strict();
/** Stages that can pay the referrer. Before a price exists only fixed components can be priced. */
exports.PAYABLE_STAGES = ['strategy_call', 'engaged', 'under_contract', 'settled'];
const PRE_PRICE_STAGES = ['strategy_call', 'engaged'];
exports.PartnerFeeScheduleSchema = zod_1.z
    .object({
    /** Monotonic; every referral snapshots the version it was priced under. */
    version: zod_1.z.number().int().positive(),
    /** Which template the schedule started from. Informational only; the rules below are what count. */
    template: zod_1.z.string().min(1).max(64),
    gstRateBps: bps,
    /** The partner's own fee. Null when the partner does not record it; then no payout may use `agent_fee`. */
    agentFee: exports.FeeRuleSchema.nullable(),
    /** What the referrer earns at each stage. Most partners pay on `settled` only. */
    payouts: zod_1.z
        .object({
        strategy_call: exports.FeeRuleSchema.optional(),
        engaged: exports.FeeRuleSchema.optional(),
        under_contract: exports.FeeRuleSchema.optional(),
        settled: exports.FeeRuleSchema.optional(),
    })
        .strict(),
    loading: zod_1.z
        .object({
        rateBps: bps,
        propertyTypes: zod_1.z.array(exports.PropertyTypeSchema).max(exports.PROPERTY_TYPES.length),
        appliesToAgentFee: zod_1.z.boolean(),
        appliesToPayouts: zod_1.z.boolean(),
    })
        .strict(),
    /** Used to derive the loan from the price when the loan amount is not recorded (8000 = 80%). */
    defaultLvrBps: bps,
})
    .strict()
    .superRefine((s, ctx) => {
    const issue = (path, message) => ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path, message });
    const checkRule = (rule, path, opts) => {
        if (rule.floorCents !== null && rule.capCents !== null && rule.floorCents > rule.capCents) {
            issue([...path, 'floorCents'], 'The floor cannot exceed the cap');
        }
        rule.components.forEach((c, i) => {
            const cpath = [...path, 'components', i];
            if (opts.preprice && c.kind !== 'fixed') {
                issue(cpath, 'Before a price is known, a stage can only pay a fixed amount');
            }
            if (c.kind === 'percent' && c.base === 'agent_fee') {
                if (opts.isAgentFee)
                    issue(cpath, "The partner's fee cannot be a percentage of itself");
                else if (s.agentFee === null)
                    issue(cpath, "A percentage of the partner's fee needs the partner's fee to be set");
            }
            if (c.kind === 'banded') {
                let prev = 0;
                c.bands.forEach((b, j) => {
                    if (b.upToCents === null) {
                        if (j !== c.bands.length - 1)
                            issue([...cpath, 'bands', j, 'upToCents'], 'Only the last band may be open-ended');
                        return;
                    }
                    if (b.upToCents <= prev)
                        issue([...cpath, 'bands', j, 'upToCents'], 'Band bounds must strictly ascend');
                    prev = b.upToCents;
                });
            }
        });
    };
    if (s.agentFee)
        checkRule(s.agentFee, ['agentFee'], { preprice: false, isAgentFee: true });
    let any = false;
    for (const stage of exports.PAYABLE_STAGES) {
        const rule = s.payouts[stage];
        if (!rule)
            continue;
        any = true;
        checkRule(rule, ['payouts', stage], { preprice: PRE_PRICE_STAGES.includes(stage), isAgentFee: false });
    }
    if (!any)
        issue(['payouts'], 'Set at least one payout');
    if (new Set(s.loading.propertyTypes).size !== s.loading.propertyTypes.length) {
        issue(['loading', 'propertyTypes'], 'Duplicate property type');
    }
});
// ─── Schedules by referrer ───────────────────────────────────────────────────
/**
 * A partner may keep more than one fee card. `partners/{id}.feeSchedule` is
 * the standard card; `feeScheduleVariants[key]` are named alternatives a desk
 * admin assigns to individual referrers (`members/{id}.feeScheduleKey`). A
 * referral is priced under its referrer's card when the quote is taken and
 * the quote records which card it used, so moving a referrer to another card
 * changes future quotes only. Subscriber brokers always use the standard card.
 */
exports.FEE_SCHEDULE_VARIANT_LIMIT = 20;
/** Lowercase slug: a Firestore map key and a URL segment, never a dotted path. */
exports.FeeScheduleVariantKeySchema = zod_1.z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,31}$/, 'Use a lowercase letter first, then letters, digits or _ (up to 32 characters)');
exports.FeeScheduleVariantSchema = zod_1.z
    .object({
    label: zod_1.z.string().trim().min(1).max(60),
    schedule: exports.PartnerFeeScheduleSchema,
})
    .strict();
/** What the standard card is called wherever a card is named. */
exports.STANDARD_FEE_SCHEDULE_LABEL = 'Standard';
/** A label becomes a key: "Fixed $500" -> "fixed_500", "0.2% of loan" -> "card_0_2_of_loan". Empty when nothing usable remains. */
function feeScheduleKeyFromLabel(label) {
    // Linear: no anchored `_+` patterns (CodeQL polynomial-regex finding on a label of many underscores).
    const slug = trimUnderscores(label.toLowerCase().replace(/[^a-z0-9]+/g, '_'));
    if (!slug)
        return '';
    return trimUnderscores((/^[a-z]/.test(slug) ? slug : `card_${slug}`).slice(0, 32));
}
function trimUnderscores(s) {
    let start = 0;
    let end = s.length;
    while (start < end && s[start] === '_')
        start++;
    while (end > start && s[end - 1] === '_')
        end--;
    return s.slice(start, end);
}
/** `amount * (1 + rateBps / 10000)`, rounded half-up, in integer arithmetic. */
function applyRateBps(amountCents, rateBps) {
    return Math.floor((amountCents * (10_000 + rateBps) + 5_000) / 10_000);
}
/** `amount * rateBps / 10000`, rounded half-up, in integer arithmetic. */
function percentOfBps(amountCents, rateBps) {
    return Math.floor((amountCents * rateBps + 5_000) / 10_000);
}
function loadingAppliesTo(schedule, propertyType) {
    return propertyType !== null && schedule.loading.propertyTypes.includes(propertyType);
}
function bandAmount(bands, value) {
    const band = bands.find((b) => b.upToCents === null || value <= b.upToCents) ?? bands[bands.length - 1];
    return band.amountCents;
}
function requirePositive(name, v) {
    if (v === null || !Number.isInteger(v) || v <= 0) {
        throw new RangeError(`${name} must be a positive integer number of cents`);
    }
    return v;
}
/**
 * Every computed amount must be a safe integer. Schema limits make overflow
 * unreachable for validated input; this makes it impossible to return a
 * silently rounded figure if an unvalidated value ever reaches the engine.
 */
function assertSafeCents(step, v) {
    if (!Number.isSafeInteger(v)) {
        throw new RangeError(`Fee calculation left the safe integer range at ${step}`);
    }
    return v;
}
function evaluateRule(schedule, rule, ctx, agentFeeExGst, loaded) {
    const baseValue = (base) => {
        if (base === 'agent_fee') {
            if (agentFeeExGst === null)
                throw new RangeError("agent_fee base used with no partner fee");
            return agentFeeExGst;
        }
        const price = requirePositive('purchasePriceCents', ctx.purchasePriceCents);
        if (base === 'purchase_price')
            return price;
        return ctx.loanAmountCents ?? percentOfBps(price, schedule.defaultLvrBps);
    };
    let sum = 0;
    for (const c of rule.components) {
        if (c.kind === 'fixed')
            sum += c.amountCents;
        else if (c.kind === 'percent')
            sum += assertSafeCents('percent', percentOfBps(baseValue(c.base), c.rateBps));
        else
            sum += bandAmount(c.bands, baseValue(c.base));
    }
    assertSafeCents('components', sum);
    if (loaded)
        sum = assertSafeCents('loading', applyRateBps(sum, schedule.loading.rateBps));
    if (rule.floorCents !== null)
        sum = Math.max(sum, rule.floorCents);
    if (rule.capCents !== null)
        sum = Math.min(sum, rule.capCents);
    return { exGstCents: assertSafeCents('exGst', sum), incGstCents: assertSafeCents('incGst', applyRateBps(sum, schedule.gstRateBps)) };
}
/**
 * Price one stage's payout. Returns null when the schedule pays nothing at
 * that stage. Throws RangeError when the rule needs a price and none is known.
 */
function quoteStagePayout(schedule, stage, ctx) {
    const rule = schedule.payouts[stage];
    if (!rule)
        return null;
    const loadingApplied = loadingAppliesTo(schedule, ctx.propertyType);
    const needsAgentFee = rule.components.some((c) => c.kind === 'percent' && c.base === 'agent_fee');
    const agentFee = needsAgentFee && schedule.agentFee
        ? evaluateRule(schedule, schedule.agentFee, ctx, null, loadingApplied && schedule.loading.appliesToAgentFee).exGstCents
        : null;
    return evaluateRule(schedule, rule, ctx, agentFee, loadingApplied && schedule.loading.appliesToPayouts);
}
/** Full quote once the price is known: the partner's fee and every stage payout. */
function quoteReferralFee(schedule, ctx) {
    requirePositive('purchasePriceCents', ctx.purchasePriceCents);
    if (ctx.propertyType === null) {
        // The loading depends on it, so a full quote on an unknown type could be wrong either way.
        throw new RangeError('propertyType must be known to quote a referral fee');
    }
    const loadingApplied = loadingAppliesTo(schedule, ctx.propertyType);
    const agentFee = schedule.agentFee
        ? evaluateRule(schedule, schedule.agentFee, ctx, null, loadingApplied && schedule.loading.appliesToAgentFee)
        : null;
    const payouts = {};
    for (const stage of exports.PAYABLE_STAGES) {
        const rule = schedule.payouts[stage];
        if (rule) {
            payouts[stage] = evaluateRule(schedule, rule, ctx, agentFee?.exGstCents ?? null, loadingApplied && schedule.loading.appliesToPayouts);
        }
    }
    return { scheduleVersion: schedule.version, loadingApplied, agentFee, payouts };
}
/** Total the referrer earns across all stages of a quote, GST inclusive. */
function totalPayoutIncGst(quote) {
    return Object.values(quote.payouts).reduce((a, p) => a + (p?.incGstCents ?? 0), 0);
}
// ─── Templates ───────────────────────────────────────────────────────────────
/**
 * Starting points for a new partner's schedule. Amounts are placeholders the
 * partner edits; no partner's real card lives in code (it is seeded as data).
 */
exports.FEE_SCHEDULE_TEMPLATES = {
    banded_by_price: {
        label: 'Fixed fee by price band',
        description: 'Your fee and the referral fee step up with the purchase price. No percentages.',
        schedule: {
            template: 'banded_by_price',
            gstRateBps: 1000,
            agentFee: {
                components: [{ kind: 'banded', base: 'purchase_price', bands: [
                            { upToCents: 1_000_000_00, amountCents: 15_000_00 },
                            { upToCents: 2_000_000_00, amountCents: 25_000_00 },
                            { upToCents: null, amountCents: 35_000_00 },
                        ] }],
                floorCents: null, capCents: null,
            },
            payouts: {
                settled: {
                    components: [{ kind: 'banded', base: 'purchase_price', bands: [
                                { upToCents: 1_000_000_00, amountCents: 1_000_00 },
                                { upToCents: 2_000_000_00, amountCents: 2_000_00 },
                                { upToCents: null, amountCents: 3_000_00 },
                            ] }],
                    floorCents: null, capCents: null,
                },
            },
            loading: { rateBps: 0, propertyTypes: [], appliesToAgentFee: true, appliesToPayouts: false },
            defaultLvrBps: 8000,
        },
    },
    share_of_agent_fee: {
        label: 'Share of your fee',
        description: 'The referrer earns a percentage of what you charge the client, with a minimum.',
        schedule: {
            template: 'share_of_agent_fee',
            gstRateBps: 1000,
            agentFee: { components: [{ kind: 'percent', base: 'purchase_price', rateBps: 200 }], floorCents: 15_000_00, capCents: null },
            payouts: { settled: { components: [{ kind: 'percent', base: 'agent_fee', rateBps: 1000 }], floorCents: 1_000_00, capCents: null } },
            loading: { rateBps: 0, propertyTypes: [], appliesToAgentFee: true, appliesToPayouts: false },
            defaultLvrBps: 8000,
        },
    },
    percent_of_price: {
        label: 'Percentage of the purchase price',
        description: 'The referrer earns a percentage of the price, with a floor and a cap.',
        schedule: {
            template: 'percent_of_price',
            gstRateBps: 1000,
            agentFee: null,
            payouts: { settled: { components: [{ kind: 'percent', base: 'purchase_price', rateBps: 10 }], floorCents: 500_00, capCents: 5_000_00 } },
            loading: { rateBps: 0, propertyTypes: [], appliesToAgentFee: false, appliesToPayouts: false },
            defaultLvrBps: 8000,
        },
    },
    percent_of_loan: {
        label: 'Percentage of the loan',
        description: 'The referrer earns a percentage of the loan written on the purchase.',
        schedule: {
            template: 'percent_of_loan',
            gstRateBps: 1000,
            agentFee: null,
            payouts: { settled: { components: [{ kind: 'percent', base: 'loan_amount', rateBps: 15 }], floorCents: null, capCents: null } },
            loading: { rateBps: 0, propertyTypes: [], appliesToAgentFee: false, appliesToPayouts: false },
            defaultLvrBps: 8000,
        },
    },
    flat_fee: {
        label: 'Flat fee',
        description: 'One set amount on settlement, whatever the price.',
        schedule: {
            template: 'flat_fee',
            gstRateBps: 1000,
            agentFee: null,
            payouts: { settled: { components: [{ kind: 'fixed', amountCents: 1_000_00 }], floorCents: null, capCents: null } },
            loading: { rateBps: 0, propertyTypes: [], appliesToAgentFee: false, appliesToPayouts: false },
            defaultLvrBps: 8000,
        },
    },
    staged_rewards: {
        label: 'Rewards along the way',
        description: 'Small fixed rewards when a strategy call is held and when the client engages, then a fee on settlement.',
        schedule: {
            template: 'staged_rewards',
            gstRateBps: 1000,
            agentFee: null,
            payouts: {
                strategy_call: { components: [{ kind: 'fixed', amountCents: 50_00 }], floorCents: null, capCents: null },
                engaged: { components: [{ kind: 'fixed', amountCents: 250_00 }], floorCents: null, capCents: null },
                settled: { components: [{ kind: 'fixed', amountCents: 1_000_00 }], floorCents: null, capCents: null },
            },
            loading: { rateBps: 0, propertyTypes: [], appliesToAgentFee: false, appliesToPayouts: false },
            defaultLvrBps: 8000,
        },
    },
};
// ─── Referral stage machine ──────────────────────────────────────────────────
/** Ordered pipeline. The partner advances a referral by hand; there is no feed. */
exports.REFERRAL_PIPELINE = [
    'submitted',
    'contacted',
    'strategy_call',
    'engaged',
    'searching',
    'under_contract',
    'settled',
];
exports.REFERRAL_STAGES = [...exports.REFERRAL_PIPELINE, 'lapsed'];
exports.ReferralStageSchema = zod_1.z.enum(exports.REFERRAL_STAGES);
exports.REFERRAL_STAGE_LABELS = {
    submitted: 'Submitted',
    contacted: 'Contacted',
    strategy_call: 'Strategy call',
    engaged: 'Engaged',
    searching: 'Searching',
    under_contract: 'Under contract',
    settled: 'Settled',
    lapsed: 'Lapsed',
};
function isTerminalStage(stage) {
    return stage === 'settled' || stage === 'lapsed';
}
/**
 * Allowed moves: forward exactly one pipeline step; any non-terminal stage to
 * `lapsed`; and `lapsed` back to `contacted` (a client who comes back).
 * `settled` is final: it is what the fee is paid on.
 */
function canTransition(from, to) {
    if (from === to)
        return false;
    if (to === 'lapsed')
        return !isTerminalStage(from);
    if (from === 'lapsed')
        return to === 'contacted';
    const i = exports.REFERRAL_PIPELINE.indexOf(from);
    return i !== -1 && exports.REFERRAL_PIPELINE[i + 1] === to;
}
function nextStage(from) {
    if (isTerminalStage(from))
        return null;
    const i = exports.REFERRAL_PIPELINE.indexOf(from);
    return exports.REFERRAL_PIPELINE[i + 1] ?? null;
}
// ─── Referral input ──────────────────────────────────────────────────────────
const trimmed = (min, max) => zod_1.z.string().trim().min(min).max(max);
/**
 * What a broker submits. Both attestations are mandatory: the client agreed to
 * their details going to the partner (APP 6), and the broker disclosed that
 * they receive a referral fee (best-interests conflict disclosure). The server
 * stamps the time; the client never supplies it.
 */
exports.CreatePartnerReferralInputSchema = zod_1.z
    .object({
    partnerId: trimmed(1, 128),
    clientName: trimmed(2, 200),
    clientEmail: zod_1.z.string().trim().toLowerCase().email().max(254),
    clientMobile: zod_1.z
        .string()
        .trim()
        .max(32)
        .refine((v) => v.replace(/\D/g, '').length >= 8, 'Enter a contactable mobile number'),
    budgetCents: cents.positive().nullish(),
    propertyType: exports.PropertyTypeSchema.default('investment'),
    goal: trimmed(0, 500).nullish(),
    market: trimmed(0, 200).nullish(),
    financeStatus: trimmed(0, 200).nullish(),
    notes: trimmed(0, 2000).nullish(),
    /**
     * VALUTTEN subscribers only: the broker doc this referral is FOR, when the
     * submitter (an owner, administrator or assistant) refers on a broker's
     * behalf. Omitted = the submitter's own broker profile. The server checks
     * it against the submitter's scope; outside referrers may not send it.
     */
    referringBrokerDocId: zod_1.z
        .string()
        .trim()
        .min(1)
        .max(256)
        .refine((v) => !v.includes('/') && v !== '.' && v !== '..', 'Invalid broker')
        .optional(),
    clientConsentConfirmed: zod_1.z.literal(true),
    feeDisclosureConfirmed: zod_1.z.literal(true),
})
    .strict();
/**
 * Who referred. A VALUTTEN subscriber's broker is identified by tenant and
 * broker doc; a broker outside VALUTTEN by their partner member id. Exactly
 * one shape, never both.
 */
exports.ReferrerRefSchema = zod_1.z.discriminatedUnion('kind', [
    zod_1.z
        .object({
        kind: zod_1.z.literal('tenant_broker'),
        companyId: zod_1.z.string().min(1).max(128),
        brokerDocId: zod_1.z.string().min(1).max(256),
        uid: zod_1.z.string().min(1).max(128),
    })
        .strict(),
    zod_1.z
        .object({
        kind: zod_1.z.literal('partner_referrer'),
        partnerMemberId: zod_1.z.string().min(1).max(128),
        uid: zod_1.z.string().min(1).max(128),
    })
        .strict(),
    /**
     * Someone who referred through a tenant's public website referral form.
     * Not signed in and not a member, so there is no uid: the desk verifies
     * them (link to a member, or invite) before any fee can be quoted.
     */
    zod_1.z
        .object({
        kind: zod_1.z.literal('public_unverified'),
        name: zod_1.z.string().trim().min(1).max(200),
        email: zod_1.z.string().trim().toLowerCase().email().max(254),
        phone: zod_1.z.string().trim().max(50).nullable(),
        business: zod_1.z.string().trim().min(1).max(200),
    })
        .strict(),
]);
/** The uid behind a referrer, or null for a public (unverified) referrer. */
function referrerUid(referrer) {
    return referrer.kind === 'public_unverified' ? null : referrer.uid;
}
exports.AdvanceReferralInputSchema = zod_1.z
    .object({
    referralId: zod_1.z.string().min(1).max(128),
    /** Optimistic concurrency: the stage the desk saw. A mismatch is refused, never merged. */
    fromStage: exports.ReferralStageSchema,
    toStage: exports.ReferralStageSchema,
    /** Required to reach `under_contract` or `settled`: the fee is priced on it. */
    purchasePriceCents: cents.positive().nullish(),
    /** The loan written on the purchase, when the partner knows it. Otherwise derived from price x default LVR. */
    loanAmountCents: cents.positive().nullish(),
    propertyType: exports.PropertyTypeSchema.nullish(),
    note: trimmed(0, 1000).nullish(),
})
    .strict();
/** Stages at which the purchase price must be known. */
exports.PRICE_REQUIRED_STAGES = ['under_contract', 'settled'];
// ─── Website referrals, verification, duplicates, CRM link, client reward ───
// Website forms platform (docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §2.7, §7).
/**
 * Whether the desk has confirmed who referred. Referrals made by a signed-in
 * member or subscriber broker are `verified` at creation; a public website
 * referral starts `unverified`, and fee quotes and ledger entries refuse
 * until the desk verifies it (link to a member, or invite the referrer).
 * Absent on referrals made before this field existed: read as `verified`.
 */
exports.REFERRAL_VERIFICATION_STATES = ['verified', 'unverified', 'rejected'];
exports.ReferralVerificationSchema = zod_1.z.enum(exports.REFERRAL_VERIFICATION_STATES);
function referralVerification(doc) {
    const v = doc.verification;
    return v === 'unverified' || v === 'rejected' ? v : 'verified';
}
/** A link from a referral to a deal in the operating company's CRM, set by the desk. */
exports.ReferralCrmLinkSchema = zod_1.z
    .object({
    provider: zod_1.z.string().min(1).max(32),
    dealId: zod_1.z.string().min(1).max(128),
    linkedBy: zod_1.z.string().min(1).max(128),
    linkedAt: zod_1.z.unknown(),
})
    .strict();
/**
 * What happens when a second referral arrives for a client the partner
 * already has:
 *   first_wins    the second is refused (today's behaviour, the default)
 *   desk_decides  the second is kept with `duplicateOf`; the desk resolves it
 *   split         both kept; every payable stage fee (strategy_call, engaged,
 *                 under_contract, settled) splits by `splitPercent`
 */
exports.DUPLICATE_RULE_MODES = ['first_wins', 'desk_decides', 'split'];
exports.DuplicateRuleModeSchema = zod_1.z.enum(exports.DUPLICATE_RULE_MODES);
exports.PartnerDuplicateRuleSchema = zod_1.z
    .object({
    mode: exports.DuplicateRuleModeSchema,
    /** `split` only: whole-percent share of the FIRST referral, 1 to 99. */
    splitPercent: zod_1.z.number().int().min(1).max(99).optional(),
})
    .strict()
    .superRefine((r, ctx) => {
    if (r.mode === 'split' && r.splitPercent === undefined) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['splitPercent'], message: 'Set the split percentage' });
    }
    if (r.mode !== 'split' && r.splitPercent !== undefined) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['splitPercent'], message: 'A split percentage applies to the split rule only' });
    }
});
exports.DEFAULT_PARTNER_DUPLICATE_RULE = Object.freeze({ mode: 'first_wins' });
/** A partner's stored rule, or the default when absent or invalid. */
function partnerDuplicateRule(raw) {
    const parsed = exports.PartnerDuplicateRuleSchema.safeParse(raw);
    return parsed.success ? parsed.data : exports.DEFAULT_PARTNER_DUPLICATE_RULE;
}
/**
 * Split one payable stage fee (strategy_call, engaged, under_contract or
 * settled) between the first and the duplicate referral, in
 * integer cents. `splitPercent` is the first referral's share; the duplicate
 * gets the rounded-down remainder share and any odd cent goes to the first,
 * so the two parts always add up to the total exactly.
 */
function splitSettlementCents(totalCents, splitPercent) {
    if (!Number.isSafeInteger(totalCents) || totalCents < 0)
        throw new RangeError('totalCents must be a non-negative integer');
    if (!Number.isInteger(splitPercent) || splitPercent < 1 || splitPercent > 99) {
        throw new RangeError('splitPercent must be an integer from 1 to 99');
    }
    const secondCents = Math.floor((totalCents * (100 - splitPercent)) / 100);
    return { firstCents: totalCents - secondCents, secondCents };
}
/**
 * Which CRM stages count as `contacted` and `settled` for this partner's
 * operating company, used by crmStageEventConsumer to advance linked
 * referrals. Stage names as the CRM reports them.
 */
exports.PartnerCrmStageMapSchema = zod_1.z
    .object({
    provider: zod_1.z.string().min(1).max(32),
    contacted: zod_1.z.array(zod_1.z.string().trim().min(1).max(120)).max(50),
    settled: zod_1.z.array(zod_1.z.string().trim().min(1).max(120)).max(50),
})
    .strict();
/** An optional reward to the referred client on settlement, issued through the gift card flow. */
exports.PartnerClientRewardSchema = zod_1.z
    .object({
    enabled: zod_1.z.boolean(),
    amountCents: cents,
    trigger: zod_1.z.literal('settled'),
})
    .strict();
/**
 * Ledger entry kinds. Entries written before kinds existed carry no `kind`
 * and are referral fees (ledgerEntryKind).
 */
exports.PARTNER_LEDGER_ENTRY_KINDS = ['referral_fee', 'client_reward'];
exports.PartnerLedgerEntryKindSchema = zod_1.z.enum(exports.PARTNER_LEDGER_ENTRY_KINDS);
function ledgerEntryKind(doc) {
    return doc.kind === 'client_reward' ? 'client_reward' : 'referral_fee';
}
